import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { KeyRound, Loader2, PlayCircle, RefreshCw, ShieldCheck, X } from "lucide-react";

/** Tela de login (computador): mostra o QR + código de 6 números e espera o celular liberar (igual WhatsApp Web).
 *  O QR vale 2 minutos e é renovado sozinho algumas vezes. `aoEntrar(user)` é chamado quando o celular libera. */
export default function EntrarComCelular({ aoEntrar, voltar }) {
  const [p, setP] = useState(null);           // { pedido, segredo, codigo, segundos }
  const [resta, setResta] = useState(0);
  const [estado, setEstado] = useState("carregando");   // carregando | aguardando | expirado | recusado | entrando | erro
  const [erro, setErro] = useState("");
  const renovacoes = useRef(0);
  // vídeo "como funciona": abre sozinho na 1ª vez neste computador; depois fica o botão
  const VISTO = "iep-video-qr-visto";
  const [video, setVideo] = useState(() => { try { return localStorage.getItem(VISTO) !== "1"; } catch { return false; } });
  const fecharVideo = () => { setVideo(false); try { localStorage.setItem(VISTO, "1"); } catch { /* sem armazenamento */ } };

  const gerar = useCallback(async () => {
    setEstado("carregando"); setErro("");
    try {
      const r = await api.post("/auth/qr/novo");
      setP(r.data); setResta(r.data.segundos); setEstado("aguardando");
    } catch (e) { setErro(formatApiError(e.response?.data?.detail)); setEstado("erro"); }
  }, []);

  useEffect(() => { gerar(); }, [gerar]);

  // contagem regressiva; quando acaba, renova sozinho até 3 vezes (depois espera o clique)
  useEffect(() => {
    if (estado !== "aguardando") return undefined;
    const t = setInterval(() => setResta((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [estado]);
  useEffect(() => {
    if (estado === "aguardando" && resta === 0 && p) {
      if (renovacoes.current < 3) { renovacoes.current += 1; gerar(); } else setEstado("expirado");
    }
  }, [resta, estado, p, gerar]);

  // pergunta ao servidor a cada 2 s se o celular já liberou
  useEffect(() => {
    if (estado !== "aguardando" || !p) return undefined;
    let vivo = true;
    const t = setInterval(async () => {
      try {
        const r = await api.post("/auth/qr/esperar", { pedido: p.pedido, segredo: p.segredo });
        if (!vivo) return;
        if (r.data.status === "entrou") { setEstado("entrando"); aoEntrar(r.data.user); }
        else if (r.data.status === "recusado") setEstado("recusado");
      } catch (e) {
        if (vivo && e.response?.status === 403) { setErro(formatApiError(e.response?.data?.detail)); setEstado("erro"); }
      }
    }, 2000);
    return () => { vivo = false; clearInterval(t); };
  }, [estado, p, aoEntrar]);

  const deNovo = () => { renovacoes.current = 0; gerar(); };
  const link = p ? `${window.location.origin}/entrar-computador?p=${encodeURIComponent(p.pedido)}` : "";
  const mmss = `${Math.floor(resta / 60)}:${String(resta % 60).padStart(2, "0")}`;

  return (
    <div className="animate-fade-up" data-testid="qr-login">
      <h2 className="font-display text-3xl font-bold text-slate-900">Entrar com o celular</h2>
      <p className="text-slate-500 mt-2 text-sm">Sem digitar senha: use o celular em que você já está logada na plataforma.</p>
      <button type="button" onClick={() => setVideo(true)} className="mt-2 mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E9E2A] hover:underline" data-testid="qr-video-botao">
        <PlayCircle className="h-4 w-4" /> Ver vídeo: como funciona (1 min)
      </button>

      <div className="grid place-items-center rounded-2xl border border-slate-200 bg-white p-5">
        {estado === "carregando" && <div className="grid h-[220px] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>}
        {estado === "aguardando" && p && (
          <>
            <QRCodeSVG value={link} size={220} level="M" marginSize={1} data-testid="qr-code" />
            <div className="mt-4 text-center">
              <div className="text-xs text-slate-500">ou digite no celular o código</div>
              <div className="font-mono text-3xl font-bold tracking-[0.25em] text-[#232D4F]" data-testid="qr-codigo">{p.codigo.slice(0, 3)} {p.codigo.slice(3)}</div>
              <div className="mt-1 text-xs text-slate-400">vale por {mmss}</div>
            </div>
          </>
        )}
        {estado === "entrando" && <div className="grid h-[220px] place-items-center text-center"><div><Loader2 className="mx-auto h-8 w-8 animate-spin text-[#1E9E2A]" /><p className="mt-3 font-semibold text-slate-700">Liberado! Entrando…</p></div></div>}
        {["expirado", "recusado", "erro"].includes(estado) && (
          <div className="grid h-[220px] place-items-center text-center">
            <div>
              <p className="text-sm text-slate-600">{estado === "recusado" ? "O acesso foi recusado no celular." : estado === "erro" ? erro : "O código expirou."}</p>
              <Button onClick={deNovo} className="mt-4 text-white" style={{ background: "#232D4F" }} data-testid="qr-gerar"><RefreshCw className="mr-2 h-4 w-4" /> Gerar outro código</Button>
            </div>
          </div>
        )}
      </div>

      <ol className="mt-6 space-y-2.5 text-sm text-slate-600">
        <li className="flex gap-2.5"><span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-green-50 text-xs font-bold text-[#1E9E2A]">1</span><span>Abra a <strong>câmera do celular</strong> e aponte para o QR code.</span></li>
        <li className="flex gap-2.5"><span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-green-50 text-xs font-bold text-[#1E9E2A]">2</span><span>Toque no link que aparecer e depois em <strong>Liberar este computador</strong>.</span></li>
        <li className="flex gap-2.5"><span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-green-50 text-xs font-bold text-[#1E9E2A]">3</span><span>A câmera não leu? No celular, abra a plataforma, toque na sua foto → <strong>Entrar no computador</strong> e digite o código.</span></li>
      </ol>
      <p className="mt-4 flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2.5 text-xs text-slate-500"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#1E9E2A]" /> O código muda a cada 2 minutos e só funciona uma vez. Nunca leia um QR code que outra pessoa te mandou.</p>

      {video && createPortal(
        <div className="fixed inset-0 z-[300] grid place-items-center bg-black/75 p-4 sm:p-8" onClick={fecharVideo} data-testid="qr-video">
          <div className="relative w-full max-w-4xl overflow-hidden rounded-2xl bg-black shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-2 bg-[#232D4F] px-4 py-2.5 text-white">
              <span className="text-sm font-semibold">Como entrar no computador com o celular</span>
              <button type="button" onClick={fecharVideo} className="rounded-full p-1 hover:bg-white/15" title="Fechar"><X className="h-5 w-5" /></button>
            </div>
            <video src="/videos/entrar-com-celular-v4.mp4" poster="/videos/entrar-com-celular-v4.jpg" controls autoPlay playsInline controlsList="nodownload"
              className="aspect-video w-full bg-black" onEnded={fecharVideo} />
            <button type="button" onClick={fecharVideo} className="w-full bg-[#1E9E2A] py-3 text-sm font-semibold text-white hover:bg-green-700">Já entendi, mostrar o QR code</button>
          </div>
        </div>, document.body)}

      <button onClick={voltar} className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E9E2A] hover:underline" data-testid="qr-voltar">
        <KeyRound className="h-4 w-4" /> Entrar com e-mail e senha
      </button>
    </div>
  );
}
